import { Text } from "@amzn/stencil-react-components/text";
import { View } from "@amzn/stencil-react-components/layout";
import React, { Fragment, ReactNode, useEffect, useState } from "react";

import { UserPermissions } from "@/services/user-permissions/user-permissions";

interface AuthLoadingProps {
  children: ReactNode;
}

type AuthState = "loading" | "success" | "error";

/**
 * Gates the entire app behind UserPermissions.authorize().
 * Renders a blank page while loading (no spinner — avoids double-loader
 * when navigating from another app). Shows an error message on failure.
 */
const AuthLoading: React.FC<AuthLoadingProps> = ({ children }) => {
  const [state, setState] = useState<AuthState>("loading");
  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    UserPermissions.current
      .authorize()
      .then(() => setState("success"))
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : "Authorization failed";
        setErrorMessage(message);
        setState("error");
      });
  }, []);

  if (state === "loading") {
    return <Fragment />;
  }

  if (state === "error") {
    return (
      <View padding="2rem">
        <Text color="error" fontSize="T200">
          {errorMessage}
        </Text>
      </View>
    );
  }

  return <Fragment>{children}</Fragment>;
};

export default AuthLoading;
